import type {
  CertificateSubscriptionOffer,
  CertificateSubscriptionPrice,
  CertificateTile,
} from '@mc/api'
import { CertificateProgramTestimonialSource } from '@mc/api'
import { paths } from '@mc/paths'
import { isCertEligibleForSubscription } from 'pages/certificates/annualpass/subscriptionEligibility'
import type {
  Testimonial,
  TestimonialCourse,
} from 'pages/certificates/components/MarketingCertPage/LinkedInTestimonialsSection'
import type { SubscriptionPromoVariables } from 'pages/certificates/promo'

// The subscription checkout entry point. All subscription CTAs on the landing
// page must use this single constant.
export const SUBSCRIPTION_SIGNUP_HREF = paths.certificatesAnnualPassCheckout

// Analytics identity of the subscription product (event spec: Certificates -
// Subscriptions Entry Point Test). TODO(MCCERTS-2619): drop the product_id
// fallback once the backend exposes a numeric id in the offer's analytics map
// (or analytics signs off on slug ids).
export const SUBSCRIPTION_PRODUCT_NAME = 'Certificates Annual Pass'
const FALLBACK_PRODUCT_ID = 0

// Certs excluded from the subscription offering are filtered out of the LP
// catalog carousel (see subscriptionEligibility). TODO(MCCERTS-2548): follow up
// with backend on whether the catalog endpoint can take a subscription-scoped
// filter so this client-side exclusion can be removed.
export const getSubscriptionCarouselTiles = (
  tiles: CertificateTile[] | undefined,
) => tiles?.filter((tile) => isCertEligibleForSubscription(tile.slug))

/**
 * Candidate discount slugs for the subscription offer endpoint: the coupon
 * carried by whichever promo payload won (site-wide or per-surface), plus any
 * vanity slugs the page collected (`promo_id` query, `cert_promo_ids` cookie).
 * The payload coupon is always present when there is a payload — the schema
 * requires `productCoupon`, so a payload can never advertise a percentage
 * without naming the coupon that charges it. There is no frontend membership
 * logic either: the AAP member discount is an AAP-targeted GrowthBook rule on
 * `mccerts-promo-subscription` serving its own promo block. Eligibility and
 * the best-price pick stay server-side: the backend applies the winning coupon
 * if the viewer qualifies and reports it via `winning_discount_slug`.
 */
export const getSubscriptionDiscountCandidates = (
  promoVariables?: SubscriptionPromoVariables | null,
  vanitySlugs: Array<string | null | undefined> = [],
): string[] => {
  const candidates = [
    promoVariables?.promo.productCoupon.default,
    ...vanitySlugs,
  ]
  return [...new Set(candidates.filter((slug): slug is string => !!slug))]
}

// Currencies with no decimal subunit — amounts arrive in whole units.
// Mirrors backend ZERO_DECIMAL_CURRENCIES (stripe_processor.rb); see the
// geo-currency-pricing reference.
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF',
  'BYR',
  'CLP',
  'DJF',
  'GNF',
  'GWP',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
  'HUF',
])

export const subunitsToMajorUnits = (
  amountSubunits: number,
  currency: string,
) =>
  ZERO_DECIMAL_CURRENCIES.has(currency) ? amountSubunits : amountSubunits / 100

/**
 * Single definition of the viewer's subscribed state, shared by every
 * component that suppresses purchase CTAs.
 */
export const isViewerSubscribed = (
  offer: CertificateSubscriptionOffer | undefined,
) => offer?.viewer?.isSubscribed ?? false

/**
 * Display price for a backend-localized price object: discounted price when a
 * discount applies, list price otherwise. Undefined when the offer has not
 * provided one — callers must hide price UI rather than invent a value.
 * No non-rounded fallback: the backend emits each `*Rounded` field alongside its
 * raw sibling, so a rounded value is always present when its raw one is.
 */
export const getDisplayPrice = (
  price: CertificateSubscriptionPrice | undefined,
) => price?.formattedNetPriceRounded ?? price?.formattedPriceRounded

/**
 * A discount is only real when the (rounded) net price exists and is below the
 * (rounded) list price. Shared by the plan comparison and the CM hero selector
 * so both suppress a pointless "$799 $799" strikethrough
 */
export const hasDiscount = (
  netPrice: string | undefined,
  listPrice: string | undefined,
) => !!netPrice && !!listPrice && netPrice !== listPrice

/**
 * Product Viewed payload resolved from the live offer. Null until the offer
 * carries real pricing — the event must never fire with fabricated values.
 */
export const getSubscriptionProductAnalytics = (
  offer: CertificateSubscriptionOffer | undefined,
) => {
  const currency = offer?.subscriptionPlan?.pricing?.currency
  const listPrice = offer?.analytics?.listPrice
  if (typeof listPrice !== 'number' || !currency) {
    return null
  }

  // `productId` can still carry the plan slug on a pre-rename payload, so the
  // numeric parse may fail (MCCERTS-2740).
  const offerProductId = Number(offer?.analytics?.productId)

  return {
    product_id: Number.isFinite(offerProductId)
      ? offerProductId
      : FALLBACK_PRODUCT_ID,
    price: listPrice,
    currency,
    name:
      offer?.subscriptionPlan?.text?.displayName ?? SUBSCRIPTION_PRODUCT_NAME,
  }
}

// Course associations for the testimonials below — they drive the
// "Certificate earned" footer on each card. Titles are the condensed catalog
// titles; the logos are static co-brand lockups (MasterClass nav shield |
// partner) hosted on static.masterclass.com, not catalog-served partner logos
// — design calls for the nav shield, not the wordmark (MCCERTS-2794).
const BREAKTHROUGH_INNOVATION_COURSE: TestimonialCourse = {
  title: 'Breakthrough Innovation Strategy',
  partnerLogo: {
    url: 'https://static.masterclass.com/moon_condensed_black_logo.png',
    altText: 'The Moonshot Factory',
  },
}

const LEADERSHIP_UNDER_PRESSURE_COURSE: TestimonialCourse = {
  title: 'Leadership and Decision-Making Under Pressure',
  partnerLogo: {
    url: 'https://static.masterclass.com/nsf_condensed_black_logo.png',
    altText: 'The Navy SEAL Foundation',
  },
}

// Alumni testimonials for the subscription LP, alternating between the two
// certificate programs (in catalog order). The copy is a verbatim snapshot of
// the LinkedIn testimonials each certificate serves on its own detail page
// (production certificate-programs API), with the course association added
// frontend-side. TODO(MCCERTS-2726): replace with a catalog-level testimonials
// aggregation once we're ready to extend the API.
export const ALUMNI_TESTIMONIALS: Testimonial[] = [
  {
    source: CertificateProgramTestimonialSource.LINKEDIN,
    quote:
      "Most importantly, I've learned that moonshot thinking isn't just about technology—it's a mindset of curiosity, resilience, and courage that can transform how I approach any challenge.",
    body: "One of the things I enjoy most is learning new things. Innovation is not new for me, but the Moonshot mindset is.\nThrough this course, I've learned to embrace audacious goals that push beyond incremental thinking, to tackle the hardest problems first (\"kill the monkey\") and to use rapid prototyping and clear kill criteria to learn fast and fail forward.\nI've discovered how to map systems and identify high-leverage points for change. If you're interested in innovation, I highly recommend this course.",
    avatarUrl: 'https://static.masterclass.com/lit-ana-gomez.jpeg',
    sourceUrl:
      'https://www.linkedin.com/posts/ana-maria-gomez-sanabria-4a285182_moonshot-activity-7392297282957836288-6-sU/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA72NKABcjtguIZB__oV9VbY3H0Y9gCo5p4',
    customer: {
      name: 'Ana M. Gómez Sanabria',
      title: 'Brand Strategist',
    },
    course: BREAKTHROUGH_INNOVATION_COURSE,
  },
  {
    source: CertificateProgramTestimonialSource.LINKEDIN,
    quote:
      'The day you think you got everything figured out is the day you stop learning.',
    body: "I'm excited to share that I've just completed the MasterClass Certificates - Leadership and Decision-Making Under Pressure. This program has been an inspiring journey into how elite US Navy SEAL leadership principles can be applied in business environments. A key takeaway: the C3+ approach - Courage, Compassion, and Communication - a simple yet powerful framework for leading teams through complexity and pressure with purpose and clarity. Continuous learning truly shapes how we lead, decide, and empower others!",
    avatarUrl: 'https://static.masterclass.com/lit-morten-drost.jpeg',
    sourceUrl:
      'https://www.linkedin.com/posts/morten-refsgaard-drost_strategicleadership-leadershipcommunication-activity-7389941409434464256-tvPy/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA72NKABcjtguIZB__oV9VbY3H0Y9gCo5p4',
    customer: {
      name: 'Morten Refsgaard Drost',
      title: 'R&D Manager',
    },
    course: LEADERSHIP_UNDER_PRESSURE_COURSE,
  },
  {
    source: CertificateProgramTestimonialSource.LINKEDIN,
    quote:
      "It's often easier to make something 10 times better than 10 percent better. Bold ideas force clarity. Incremental ideas invite noise.",
    body: "Instead of planning small improvements, I'm studying how Google X approaches big ones - how they kill bad ideas early, reward learning, and fall in love with the problem, not the solution. 2026 will reward people who think bigger, simplify faster, and build from first principles. A moonshot mindset beats a to-do list.",
    avatarUrl: 'https://static.masterclass.com/lit-karl-baron.jpeg',
    sourceUrl:
      'https://www.linkedin.com/posts/karl-baron_%F0%9D%97%A6%F0%9D%97%B6%F0%9D%97%B4%F0%9D%97%BB%F0%9D%97%AE%F0%9D%97%B9-%F0%9D%97%A1%F0%9D%97%BC%F0%9D%97%B6%F0%9D%98%80%F0%9D%97%B2-%F0%9D%97%A0%F0%9D%97%BC%F0%9D%97%BC%F0%9D%97%BB%F0%9D%98%80%F0%9D%97%B5%F0%9D%97%BC-activity-7403538805536071680-9fxT/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA72NKABcjtguIZB__oV9VbY3H0Y9gCo5p4',
    customer: {
      name: 'Karl Baron',
      title: 'Founder',
    },
    course: BREAKTHROUGH_INNOVATION_COURSE,
  },
  {
    source: CertificateProgramTestimonialSource.LINKEDIN,
    quote:
      'Taking a leadership course through MasterClass given by Navy SEALs was very much a refresher for me. The stories brought back memories of my time in the Marines, as well as some experiences in business.',
    body: 'People do not struggle with strategic decision-making; they make bad decisions, choose bad advisors, or miss opportunities right in front of their eyes. I have some fond memories of seeing great leadership at work and being able to accomplish amazing things. I have plenty of not so fond memories where experiences of poor leadership caused issues that could have been avoided. I recommend this class, especially in the case where you are in leadership and are quite confident you do not need this.',
    avatarUrl: 'https://static.masterclass.com/lit-dough-needham.jpeg',
    sourceUrl:
      'https://www.linkedin.com/posts/dougneedham_leadership-activity-7403814376006156288-sesm/',
    customer: {
      name: 'Doug Needham',
      title: 'Chief Technical Advisor',
    },
    course: LEADERSHIP_UNDER_PRESSURE_COURSE,
  },
  {
    source: CertificateProgramTestimonialSource.LINKEDIN,
    quote:
      'Education platforms are moving from content consumption to skill activation. The value proposition is no longer the celebrity, but the ability to apply what you learn',
    body: "This year, I decided to give myself something different for my birthday: education. I started with something that immediately caught my attention back in September: MasterClass' new certification business model expansion. Specifically the partnership with X, The Moonshot Factory. As a design leader, I was intrigued by how MasterClass is expanding beyond inspiration into structured, credentialed learning. Their use of AI throughout the class was also a really engaging way to both reflect along the journey but also receive real time feedback towards my casestudy / class assignment. It challenged me to think about how teams can operationalize exploration, embed experimentation into culture, and balance bold ideas with systemic discipline.",
    avatarUrl: 'https://static.masterclass.com/lit-meghan-preiss.jpeg',
    sourceUrl:
      'https://www.linkedin.com/posts/meghanpreiss_this-year-i-decided-to-give-myself-something-activity-7391833746561323008-fxqx/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA72NKABcjtguIZB__oV9VbY3H0Y9gCo5p4',
    customer: {
      name: 'Meghan Preiss',
      title: 'Service Design & Design Strategist',
    },
    course: BREAKTHROUGH_INNOVATION_COURSE,
  },
  {
    source: CertificateProgramTestimonialSource.LINKEDIN,
    quote:
      'This was certainly welcomed! To either give me confidence in what I knew or keep my feet on the ground and stay humble.',
    body: "I've just finished my first MasterClass Certificate with the Navy Seal Foundation. Having almost either been reading or listening to books by Jocko Willink, Leif Babin, Admiral William McRaven, L. David Marquet and countless hours listening to the Jocko Podcast for the last 5 years, this was certainly welcomed!",
    avatarUrl: 'https://static.masterclass.com/lit-olafur.jpeg',
    sourceUrl:
      'https://www.linkedin.com/posts/%C3%B3lafur-m%C3%A1r-s%C3%ADmonarson-17268a194_ive-just-finished-my-first-masterclass-certificate-activity-7388346352054816768-iLLD/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAA72NKABcjtguIZB__oV9VbY3H0Y9gCo5p4',
    customer: {
      name: 'Ólafur Már Símonarson',
      title: 'Service Manager',
    },
    course: LEADERSHIP_UNDER_PRESSURE_COURSE,
  },
]
